import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Table, Form, Button, Pagination, Row, Col, Spinner, Image } from 'react-bootstrap';
import { withRouter, Link } from 'react-router-dom';
import api from '../utils/api';
import orgmsLogo from '../images/ggsf_logo.jpeg';
import moment from 'moment-timezone';
import { AuthContext } from '../context/AuthContext';

import jsPDF from 'jspdf';
import 'jspdf-autotable';

class DonationHistorySummary extends Component {
    static contextType = AuthContext; // Attach the context to the class
    constructor(props) {
        super(props);
        
        const { searchCriteria, currentPage: memberSearchPage } = props.location.state || {};
        this.state = {
            receipts: [],
            loading: true,
            error: null,
            currentPage: 1,
            totalReceipts: 0,
            receiptsPerPage: 10, // Default to 10 if not provided
            dateRange: 'all', // Default date range
            customStartDate: '',
            customEndDate: '',
            totalDonation: 0,
            memberDetails: {},
            orgDetails: {},
            searchCriteria: searchCriteria || {},
            memberSearchPage: memberSearchPage || 1,
            showSummary: false, // State variable to control summary visibility
            imageLoaded: false,
            sortOrder: 'DESC',
            isEmailSending: false // New state variable to control email sending status
        };
        this.reportRef = React.createRef();
    }

    componentDidMount() {
        // Fetch receipts based on current state or URL parameter
        const { match, location } = this.props;
        const queryParams = new URLSearchParams(location.search);
        const pageNumber = parseInt(queryParams.get('pagerds')) || 1;
        const dateRangeParam = queryParams.get('daterdr') || 'all';
        this.setState({ currentPage: pageNumber, dateRange: dateRangeParam });
        const memberId = match.params.memberId; // Ensure this matches the route parameter
        this.setState({ memberId }, () => {
            this.fetchData();
        });
        this.preloadImage(orgmsLogo);
        this.setDropdownWidth();
        // Now that the context is available, we can access resultsPerPage
        const limit = parseInt(this.context.resultsPerPage, 10);
        this.setState({ receiptsPerPage: limit || 10 });
    }

    componentDidUpdate() {
        this.setDropdownWidth();
    }

    setDropdownWidth = () => {
        const selectElement = document.getElementById('dateRangeSelect');
        if (selectElement) {
            const options = selectElement.options;
            let maxWidth = 0;

            for (let i = 0; i < options.length; i++) {
                const optionWidth = this.getTextWidth(options[i].text, window.getComputedStyle(selectElement).font);
                if (optionWidth > maxWidth) {
                    maxWidth = optionWidth;
                }
            }

            // Add some padding to the calculated width
            selectElement.style.width = `${maxWidth + 50}px`;
        }
    }

    getTextWidth = (text, font) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = font;
        return context.measureText(text).width;
    }

    preloadImage = (src) => {
        const img = new window.Image();
        img.src = src;
        img.onload = this.handleImageLoad;
    };

    handleImageLoad = () => {
        this.setState({ imageLoaded: true });
    };

    handleBack = () => {
        const { searchCriteria, memberSearchPage } = this.state;
        if (searchCriteria) {
            this.props.history.push({
                pathname: '/members',
                state: { searchCriteria, currentPage: memberSearchPage }
            });
        }
        else { this.props.history.goBack(); }
    };

    fetchData = () => {
        const { memberId } = this.state;
        this.setState({ loading: true });

        Promise.all([
            api.get(`/api/members/${memberId}`),
            api.get('/api/appconfig'),
            this.getReceipts()
        ])
            .then(([memberRes, orgRes, receiptsRes]) => {
                const { data: memberDetails } = memberRes.data;
                const { data: orgDetails } = orgRes.data;
                const { data: receipts, totalDonation, total } = receiptsRes.data;

                this.setState({
                    memberDetails,
                    orgDetails,
                    receipts,
                    totalReceipts: total,
                    totalDonation,
                    loading: false,
                    error: null
                });
            })
            .catch(error => {
                this.setState({ error: error.message, loading: false });
            });
    }

    getReceipts = () => {
        const { currentPage, receiptsPerPage, dateRange, customStartDate, customEndDate, memberId, sortOrder } = this.state;
        let path = `/api/members/${memberId}/receipts?dateRange=${dateRange}&page=${currentPage}&limit=${receiptsPerPage}&sortOrder=${sortOrder}`;
        if (dateRange === 'custom' && customStartDate && customEndDate) {
            path += `&startDate=${customStartDate}&endDate=${customEndDate}`;
        }
        return api.get(path);
    }

    getAllReceiptsForPrint = async () => {
        const { dateRange, customStartDate, customEndDate, memberId, sortOrder } = this.state;
        let path = `/api/members/${memberId}/receipts?dateRange=${dateRange}&sortOrder=${sortOrder}`;
        if (dateRange === 'custom' && customStartDate && customEndDate) {
            path += `&startDate=${customStartDate}&endDate=${customEndDate}`;
        }
        try {
            const response = await api.get(path);
            // Update state for UI
            this.setState({
                receipts: response.data.data,
                totalDonation: response.data.totalDonation,
                totalReceipts: response.data.total,
                receiptsPerPage: response.data.total
            });
            // Return data for PDF
            return response.data;
        } catch (error) {
            console.error('Error fetching receipts:', error);
            return { data: [], totalDonation: 0, total: 0 };
        }
    }

    handleDateRangeChange = (event) => {
        const dateRange = event.target.value;
        this.setState({ dateRange, currentPage: 1 }, this.fetchData);
    }

    getModeOfPaymentText = (modeOfPayment) => {
        switch (modeOfPayment) {
            case 1: return 'Cash';
            case 2: return 'Check';
            case 3: return 'Credit Card';
            case 4: return 'Other';
            default: return 'Unknown';
        }
    };

    formatDate = (dateStr) => {
        // Check if dateStr is undefined, null, or an empty string
        if (!dateStr) {
            return ''; // Return an empty string or any default value if dateStr is not valid
        }

        // Split the date string into its components
        const [year, month, day] = dateStr.split('-');

        // Return the formatted date as MM/DD/YYYY
        return `${month}/${day}/${year}`;
    };

    getDateRangeLabel = (dateRange) => {
        switch (dateRange) {
            case 'all':
                return 'Life Time';
            case 'ytd':
                return 'Year-to-Date';
            case 'lastYear':
                return 'Last Year';
            case 'custom':
                return 'Custom Range';
            default:
                return 'Year-to-Date'; // Default case if no match
        }
    }

    handleCustomDateChange = (event) => {
        const { name, value } = event.target;
        this.setState({ [name]: value });
    }

    handleCustomDateSubmit = () => {
        const { customStartDate, customEndDate } = this.state;
        const minDate = moment('1992-01-01'); // January 1, 1992

        if (!customStartDate || !customEndDate) {
            alert('Start Date and End Date cannot be null.');
            return;
        }

        if (moment(customStartDate).isBefore(minDate) || moment(customEndDate).isBefore(minDate)) {
            alert('Start Date and End Date cannot be before 1992.');
            return;
        }

        this.fetchData();
    }

    handlePageChange = (pageNumber) => {
        this.setState({ currentPage: pageNumber }, this.fetchData);
    }

    handleSortOrderChange = (event) => {
        this.setState({ sortOrder: event.target.value }, this.fetchData);
    }

    handlePrintCurrentPage = () => {
        this.setState({ showSummary: true }, () => {
            window.print();
            this.setState({ showSummary: false }); // Hide summary after printing
        });
    };

    handlePrintAllPages = async () => {
        // Store the current pagination state

        this.setState(prevState => ({
            previousPagination: {
                currentPage: prevState.currentPage,
                receiptsPerPage: prevState.receiptsPerPage,
                totalReceipts: prevState.totalReceipts
            }
        }), async () => {
            // Fetch all receipts (omit pagination parameters)
            await this.getAllReceiptsForPrint();
            // show summary
            this.setState({ showSummary: true });
            // Simulate printing (this could be a print dialog or any other print action)
            window.print();

            // Restore the previous pagination state
            this.restorePreviousPaginationState();
        });
    }


    restorePreviousPaginationState = () => {
        const { previousPagination } = this.state;

        this.setState({
            currentPage: previousPagination.currentPage,
            receiptsPerPage: previousPagination.receiptsPerPage,
            totalReceipts: previousPagination.totalReceipts,
            showSummary: false
        }, this.fetchData);
    }

    handleDownloadPDF = async () => {
        // Regenerate the PDF for download
        const response = await this.getAllReceiptsForPrint();
        const receipts = response.data;
        const totalDonation = response.totalDonation;
        const { orgDetails, memberDetails, dateRange } = this.state;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 14;
        const headerY = 16;
        const lineSpacing = 6;

        // --- Org details (left) ---
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(orgDetails.name || '', margin, headerY);
        doc.setFont(undefined, 'normal');
        doc.text(`${orgDetails.address || ''} ${orgDetails.address2 || ''}`, margin, headerY + lineSpacing);
        doc.text(`${orgDetails.city || ''}, ${orgDetails.state || ''} ${orgDetails.zip || ''}`, margin, headerY + lineSpacing * 2);
        doc.text(`Phone: ${orgDetails.phone || ''}`, margin, headerY + lineSpacing * 3);
        doc.text(`Tax ID: ${orgDetails.taxId || ''}`, margin, headerY + lineSpacing * 4);

        // --- Logo (center) ---
        try {
            const img = new window.Image();
            img.src = orgmsLogo;
            await new Promise(resolve => { img.onload = resolve; });
            const logoWidth = 30;
            const logoHeight = 18;
            const logoX = (pageWidth - logoWidth) / 2;
            const logoY = headerY;
            doc.addImage(img, 'JPEG', logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            // If logo fails, skip it
        }

        // --- Member details (right) ---
        doc.setFontSize(10);
        const rightX = pageWidth - margin;
        let memberY = headerY;
        doc.setFont(undefined, 'bold');
        doc.text(`Donation History for ${this.getDateRangeLabel(dateRange)}`, rightX, memberY, { align: 'right' });
        memberY += lineSpacing;
        doc.text(`Account Number: ${memberDetails.memberId || ''}`, rightX, memberY, { align: 'right' });
        doc.setFont(undefined, 'normal');
        memberY += lineSpacing;
        doc.text(`${memberDetails.first1 || ''} ${memberDetails.middle1 || ''} ${memberDetails.last1 || ''}`, rightX, memberY, { align: 'right' });
        memberY += lineSpacing;
        doc.text(`${memberDetails.first2 || ''} ${memberDetails.middle2 || ''} ${memberDetails.last2 || ''}`, rightX, memberY, { align: 'right' });
        memberY += lineSpacing;
        doc.text(`${memberDetails.address || ''} ${memberDetails.address2 || ''}, ${memberDetails.city || ''}, ${memberDetails.state || ''} ${memberDetails.zip || ''}`, rightX, memberY, { align: 'right' });

        // --- Info paragraph (full width, below header) ---
        const infoY = Math.max(headerY + lineSpacing * 5, memberY) + lineSpacing;
        doc.setFontSize(10);
        doc.text(
            'Following are the donations made by you and/or your family member. Should you notice any discrepancy, please let us know. We appreciate your financial support and patronage. Donor did not receive any material benefit for these donations.',
            margin,
            infoY,
            { maxWidth: pageWidth - 2 * margin }
        );

        // --- Table data ---
        const tableColumn = ["Receipt Num", "Receipt Date", "Payment Mode", "Reference Number", "Amount"];
        const tableRows = receipts.map(receipt => [
            receipt.receiptNum,
            this.formatDate(receipt.receiptDate),
            this.getModeOfPaymentText(receipt.modeOfPayment),
            receipt.paymentReference,
            `$${receipt.donationAmt}`
        ]);

        // --- Table ---
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: infoY + lineSpacing * 2,
            styles: { fontSize: 9 },
            theme: 'grid'
        });

        // --- Total donation ---
        const finalY = doc.lastAutoTable.finalY || infoY + lineSpacing * 2;
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(
            `Total Donation for ${this.getDateRangeLabel(dateRange)}: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDonation || 0)}`,
            margin,
            finalY + lineSpacing
        );

        // Save PDF for download
        doc.save(`donation_history_${dateRange}.pdf`);
    };

    generatePDFData = async () => {
        const response = await this.getAllReceiptsForPrint();
        const receipts = response.data;
        const totalDonation = response.totalDonation;
        const { orgDetails, memberDetails, dateRange } = this.state;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 14;
        const headerY = 16;
        const lineSpacing = 6;

        // --- Org details (left) ---
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(orgDetails.name || '', margin, headerY);
        doc.setFont(undefined, 'normal');
        doc.text(`${orgDetails.address || ''} ${orgDetails.address2 || ''}`, margin, headerY + lineSpacing);
        doc.text(`${orgDetails.city || ''}, ${orgDetails.state || ''} ${orgDetails.zip || ''}`, margin, headerY + lineSpacing * 2);
        doc.text(`Phone: ${orgDetails.phone || ''}`, margin, headerY + lineSpacing * 3);
        doc.text(`Tax ID: ${orgDetails.taxId || ''}`, margin, headerY + lineSpacing * 4);

        // --- Logo (center) ---
        try {
            const img = new window.Image();
            img.src = orgmsLogo;
            await new Promise(resolve => { img.onload = resolve; });
            const logoWidth = 30;
            const logoHeight = 18;
            const logoX = (pageWidth - logoWidth) / 2;
            const logoY = headerY;
            doc.addImage(img, 'JPEG', logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            // If logo fails, skip it
        }

        // --- Member details (right) ---
        doc.setFontSize(10);
        const rightX = pageWidth - margin;
        let memberY = headerY;
        doc.setFont(undefined, 'bold');
        doc.text(`Donation History for ${this.getDateRangeLabel(dateRange)}`, rightX, memberY, { align: 'right' });
        memberY += lineSpacing;
        doc.text(`Account Number: ${memberDetails.memberId || ''}`, rightX, memberY, { align: 'right' });
        doc.setFont(undefined, 'normal');
        memberY += lineSpacing;
        doc.text(`${memberDetails.first1 || ''} ${memberDetails.middle1 || ''} ${memberDetails.last1 || ''}`, rightX, memberY, { align: 'right' });
        memberY += lineSpacing;
        doc.text(`${memberDetails.first2 || ''} ${memberDetails.middle2 || ''} ${memberDetails.last2 || ''}`, rightX, memberY, { align: 'right' });
        memberY += lineSpacing;
        doc.text(`${memberDetails.address || ''} ${memberDetails.address2 || ''}, ${memberDetails.city || ''}, ${memberDetails.state || ''} ${memberDetails.zip || ''}`, rightX, memberY, { align: 'right' });

        // --- Info paragraph (full width, below header) ---
        const infoY = Math.max(headerY + lineSpacing * 5, memberY) + lineSpacing;
        doc.setFontSize(10);
        doc.text(
            'Following are the donations made by you and/or your family member. Should you notice any discrepancy, please let us know. We appreciate your financial support and patronage. Donor did not receive any material benefit for these donations.',
            margin,
            infoY,
            { maxWidth: pageWidth - 2 * margin }
        );

        // --- Table data ---
        const tableColumn = ["Receipt Num", "Receipt Date", "Payment Mode", "Reference Number", "Amount"];
        const tableRows = receipts.map(receipt => [
            receipt.receiptNum,
            this.formatDate(receipt.receiptDate),
            this.getModeOfPaymentText(receipt.modeOfPayment),
            receipt.paymentReference,
            `$${receipt.donationAmt}`
        ]);

        // --- Table ---
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: infoY + lineSpacing * 2,
            styles: { fontSize: 9 },
            theme: 'grid'
        });

        // --- Total donation ---
        const finalY = doc.lastAutoTable.finalY || infoY + lineSpacing * 2;
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(
            `Total Donation for ${this.getDateRangeLabel(dateRange)}: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDonation || 0)}`,
            margin,
            finalY + lineSpacing
        );

        // Return PDF as base64 for email
        return doc.output('datauristring').split(',')[1];
    };

    handleSendEmail = async () => {
        this.setState({ isEmailSending: true, showNotification: false, emailError: '' });
        const { memberDetails, dateRange } = this.state;
        const to = memberDetails?.email || '';
        const pdfData = await this.generatePDFData();
        if (!pdfData) {
            this.setState({ emailError: 'PDF generation failed.', isEmailSending: false });
            return;
        }

        const subject = `Donation History Summary: ${this.getDateRangeLabel(dateRange)}`;
        const text = `Hello ${memberDetails.first1 || ''} ${memberDetails.middle1 || ''} ${memberDetails.last1 || ''},\n\nPlease find attached your donation history summary for ${this.getDateRangeLabel(dateRange)}.\n\nThank you for your support.`;
        const pdfName = `donation_history_${dateRange}.pdf`;

        api.post('/api/send-email', {
            to,
            subject,
            text,
            pdfData,
            pdfName
        })
        .then(res => {
            if (res.data.error) {
                this.setState({
                    emailError: res.data.error,
                    showNotification: false,
                    isEmailSending: false
                });
            } else {
                this.setState({
                    showNotification: true,
                    notificationMessage: typeof res.data === 'string' ? res.data : 'Email sent successfully!',
                    emailError: '',
                    showEmailModal: false,
                    isEmailSending: false
                });
            }
        })
        .catch(error => {
            this.setState({
                emailError: error.message || 'Failed to send email.',
                showNotification: false,
                isEmailSending: false
            });
            console.error('Error sending email:', error);
        });
    };

    renderPagination() {
        const { currentPage, receiptsPerPage, totalReceipts } = this.state;
        const totalPages = Math.ceil(totalReceipts / receiptsPerPage);
        const maxPageNumbersToShow = 10; // Number of pages to display at a time
        const currentGroup = Math.ceil(currentPage / maxPageNumbersToShow);
        const startPage = (currentGroup - 1) * maxPageNumbersToShow + 1;
        const endPage = Math.min(startPage + maxPageNumbersToShow - 1, totalPages);

        if (totalPages <= 1) return null;

        return (
            <Pagination>
                <Pagination.First onClick={() => this.handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => this.handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                {startPage > 1 && (
                    <Pagination.Ellipsis onClick={() => this.handlePageChange(startPage - 1)} />
                )}

                {[...Array(endPage - startPage + 1)].map((_, index) => {
                    const pageNumber = startPage + index;
                    return (
                        <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => this.handlePageChange(pageNumber)}
                        >
                            {pageNumber}
                        </Pagination.Item>
                    );
                })}

                {endPage < totalPages && (
                    <Pagination.Ellipsis onClick={() => this.handlePageChange(endPage + 1)} />
                )}

                <Pagination.Next onClick={() => this.handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => this.handlePageChange(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
        );
    }

    render() {
        const { receipts, loading, error, totalReceipts, dateRange, customStartDate, customEndDate, totalDonation, memberDetails, orgDetails, showSummary, currentPage, searchCriteria, memberSearchPage } = this.state;


        if (loading) {
            return <Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>;
        }

        if (error) {
            return <div className="error-message">Error: {error}. Please try again later.</div>;
        }

        const donationRows = receipts.map(receipt => (
            <tr key={receipt.receiptNum}>
                <td className="align-middle">
                    <Link
                        to={{
                            pathname: `/receiptDetails/${receipt.receiptNum}`,
                            search: `?pagerds=${currentPage}&daterdr=${dateRange}`,
                            state: { searchCriteria, currentPage: memberSearchPage }
                        }}
                        className="receipt-link"
                    >
                        {receipt.receiptNum}
                    </Link>
                </td>
                <td>{this.formatDate(receipt.receiptDate)}</td>
                <td>{this.getModeOfPaymentText(receipt.modeOfPayment)}</td>
                <td>{receipt.paymentReference}</td>
                <td>${receipt.donationAmt}</td>
            </tr>
        ));

        return (
            <div>
                {this.state.showNotification && (
                    <div className="notification-message no-print small-font text-success">
                        {this.state.notificationMessage}
                    </div>
                )}
                {this.state.emailError && (
                    <div className="text-danger no-print small-font text-danger">
                        {this.state.emailError}
                    </div>
                )}
                <Button className="btn btn-secondary float-right no-print small-font" onClick={this.handleBack}>Go Back</Button>
                <Form.Group className="no-print">
                    <Form.Row className="align-items-center">
                        <Col md={6} className="d-flex align-items-center">
                            <Form.Label className="small-font date-range-dropdown font-weight-bold mr-2">Date Range</Form.Label>
                            <Form.Control
                                as="select"
                                id="dateRangeSelect"
                                value={dateRange}
                                onChange={this.handleDateRangeChange}
                                className="small-font"
                            >
                                <option value="all">All</option>
                                <option value="ytd">Year-to-Date</option>
                                <option value="lastYear">Last Year</option>
                                <option value="custom">Custom</option>
                            </Form.Control>
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                            <Form.Label className="small-font font-weight-bold mr-2">Sort by Receipt Date</Form.Label>
                            <div className="radio-box p-2">
                                <Form.Check
                                    type="radio"
                                    id="sort-desc"
                                    name="sortOrder"
                                    value="DESC"
                                    label="Descending"
                                    checked={this.state.sortOrder === 'DESC'}
                                    onChange={this.handleSortOrderChange}
                                    className="small-font"
                                    inline
                                />
                                <Form.Check
                                    type="radio"
                                    id="sort-asc"
                                    name="sortOrder"
                                    value="ASC"
                                    label="Ascending"
                                    checked={this.state.sortOrder === 'ASC'}
                                    onChange={this.handleSortOrderChange}
                                    className="small-font"
                                    inline
                                />
                            </div>
                        </Col>
                    </Form.Row>
                </Form.Group>
                {dateRange === 'custom' && (
                    <div className="no-print">
                        <Form>
                            <Row className="align-items-end">
                                <Col xs={12} md={4}>
                                    <Form.Group controlId="customStartDate">
                                        <Form.Label className="small-font font-weight-bold">Start Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="customStartDate"
                                            value={customStartDate}
                                            onChange={this.handleCustomDateChange}
                                            className="small-input form-control"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Group controlId="customEndDate">
                                        <Form.Label className="small-font font-weight-bold">End Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="customEndDate"
                                            value={customEndDate}
                                            onChange={this.handleCustomDateChange}
                                            className="small-input form-control"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={4} className="d-flex justify-content-end">
                                    <Button
                                        className="no-print btn-apply small-font mt-2 mt-md-0"
                                        onClick={this.handleCustomDateSubmit}
                                    >
                                        Apply
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                )}
                <div className="button-group no-print">
                    <Button onClick={this.handlePrintCurrentPage} className="small-font">Print</Button>
                    {totalReceipts > 10 && (
                        <Button onClick={this.handlePrintAllPages} className="no-print small-font">
                            Print All
                        </Button>
                    )}
                    <Button onClick={this.handleDownloadPDF} className="no-print small-font">
                        Download as PDF
                    </Button>
                    <Button
                        onClick={this.handleSendEmail}
                        className="no-print small-font"
                        disabled={this.state.isEmailSending || !this.state.memberDetails.email}
                    >
                        {this.state.isEmailSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </div>
                <div ref={this.reportRef}>
                    {showSummary && (
                        <div className="summary-section">
                            <div className="summary-left">
                                <p className="bold-text small-font">{orgDetails.name}</p>
                                <p className="small-font">{orgDetails.address} {orgDetails.address2}</p>
                                <p className="small-font">{orgDetails.city}, {orgDetails.state} {orgDetails.zip}</p>
                                <p className="small-font">Phone: {orgDetails.phone}</p>
                                <p className="small-font">Tax ID: {orgDetails.taxId}</p>
                            </div>
                            <div className="summary-center">
                                <div className="image-container">
                                    <Image
                                        src={orgmsLogo}
                                        alt="ORGMS Logo"
                                        fluid
                                        style={{
                                            width: '100px', // Adjust the size of the logo as needed
                                            height: 'auto'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="summary-right">
                                <p className="bold-text small-font">Donation History for {this.getDateRangeLabel(dateRange)}</p>
                                <p className="bold-text small-font">Account Number: {memberDetails.memberId}</p>
                                <p className="small-font">{memberDetails.first1} {memberDetails.middle1} {memberDetails.last1}</p>
                                <p className="small-font">{memberDetails.first2} {memberDetails.middle2} {memberDetails.last2}</p>
                                <p className="small-font">{memberDetails.address} {memberDetails.address2}, {memberDetails.city}, {memberDetails.state} {memberDetails.zip}</p>
                            </div>
                        </div>
                    )}
                    {receipts.length > 0 ? (
                        <>
                            <p className="small-font">
                                Following are the donations made by you and/or your family member. Should you notice any discrepancy, please let us know.
                                We appreciate your financial support and patronage. Donor did not receive any material benefit for these donations.
                            </p>
                            <Table striped bordered hover size="sm" className="small-font">
                                <thead>
                                    <tr>
                                        <th>Receipt Num</th>
                                        <th>Receipt Date</th>
                                        <th>Payment Mode</th>
                                        <th>Reference Number</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {donationRows}
                                </tbody>
                            </Table>
                            <p className="bold-text small-font">
                                Total Donation for {this.getDateRangeLabel(dateRange)}: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDonation || 0)}
                            </p>
                        </>
                    ) : (
                        <div className="text-center small-font">
                            <div>No donation receipts found for the selected Date Range.</div>
                        </div>
                    )}
                </div>
                {this.state.showNotification && (
    <div className="notification-message no-print small-font">
        {this.state.notificationMessage}
    </div>
)}
{this.state.emailError && (
    <div className="text-danger no-print small-font">
        {this.state.emailError}
    </div>
)}
                {totalReceipts > 10 && this.renderPagination()}
            </div>
        );
    }
}

DonationHistorySummary.propTypes = {
    match: PropTypes.object.isRequired
};

export default withRouter(DonationHistorySummary);